import { useEffect, useState } from 'react';

const INACTIVITY_MS = 8_000;

/**
 * Tracks whether a stylus is currently in use against the document.
 *
 * Implementation: listens to pointer events at the window level and
 * flips `penActive` on when a `pointerType: 'pen'` event arrives.
 * The flag stays true for [`INACTIVITY_MS`] (8s) after the last pen
 * event, then decays back to false.
 *
 * Why a decay timer instead of "off the moment the pen leaves the
 * screen": pure-web detection can't distinguish "pen is back in the
 * holster" from "pen is held a few cm away mid-stroke" — both are
 * just "no pen event." A short decay (8s) lets the pill survive the
 * brief gaps between annotation strokes without dangling around
 * indefinitely after the operator puts the pen away.
 *
 * Works on any stylus that fires pointer events with the right type
 * (S Pen, Apple Pencil over web, Wacom, etc.) — the launcher only
 * surfaces the indicator behind the `oem.samsung` gate, but the
 * detection itself is universal so the hook stays reusable.
 */
export function usePenDetector(): boolean {
  const [penActive, setPenActive] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let timer: number | undefined;

    function handle(e: PointerEvent): void {
      if (e.pointerType !== 'pen') return;
      setPenActive(true);
      if (timer !== undefined) window.clearTimeout(timer);
      timer = window.setTimeout(() => setPenActive(false), INACTIVITY_MS);
    }

    // Listening on pointermove + pointerover catches both
    // "pen is touching the screen" and "pen is hovering over the
    // screen" — Tab S10 FE fires hover events at ~3mm from the
    // glass when the S Pen is out of the holster.
    window.addEventListener('pointermove', handle, { passive: true });
    window.addEventListener('pointerover', handle, { passive: true });
    window.addEventListener('pointerdown', handle, { passive: true });

    return () => {
      window.removeEventListener('pointermove', handle);
      window.removeEventListener('pointerover', handle);
      window.removeEventListener('pointerdown', handle);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, []);

  return penActive;
}
